import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import {
  DEFAULT_HOME_BASE,
  formatMinutes,
  instructorGate,
  nextFlightLesson,
  preSoloGate,
  soloMedicalGate,
  studentGate,
  unrecordedFuelOil,
  type CreateTrainingFlightDraft,
  type GateLicence,
  type MemberLicence,
} from '@jupi/shared'
import { Icon } from '@iconify/react'
import { ApiError, IconButton, RemoteContent, Title, useAuth, useClubToday, useServerClock, useSnackbar } from '@jupi/ui'
import { useFlightLogAircraft, useFlightLogConfig, useFlightTypes } from '../../api/flightLog'
import { useLessonBookings, useLicencesOf, useProgress, useTrainingMutations } from '../../api/school'
import {
  FlightFieldsExtras,
  FuelOilConfirm,
  FlightFieldsMain,
  dateIn,
  emptyFlightForm,
  useFlightModel,
  type FlightFieldsProps,
  type FlightFormState,
} from '../../components/flightLog/FlightFields'
import { useTimeBase } from '../../components/flightLog/timeBase'

// Briefing and logging one training flight, built for a phone on the
// apron: the licence checks first, then the lesson and its checklist, then
// the flight itself, with a save bar that stays in reach.
//
// A school flight is a normal flight, so the flight part of this form IS the
// flight log's (components/flightLog/FlightFields): the same aircraft and
// flight type, the quick time entry and timeline, the aerodrome lookup
// starting from the club's home base, landings, night/IFR, fuel and oil. It
// lands in the aircraft's journey log with the instructor as pilot and the
// student as crew -- or, on a solo, the student as pilot (the API decides).
//
// The same gates run on the server; here they explain themselves before
// anyone types a time. Only the solo medical actually stops the save.

const toGate = (licences: MemberLicence[]): GateLicence[] => licences
// The flight type every school flight is logged as.
const SCHOOL_FLIGHT_TYPE = 'KOU'
// The API's upload limit (apps/api/src/lib/files.ts).
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024
const itemKey = (lessonId: number, itemCode: string) => `${lessonId}:${itemCode}`

interface SchoolFormState extends FlightFormState {
  classification: 'dual' | 'solo'
  isCrossCountry: boolean
  instrumentMinutes: string
  outsideAto: boolean
  needsRepeat: boolean
  briefingCompleted: boolean
  briefingNotes: string
}

// A school flight is dual unless the instructor says solo: two aboard.
const emptyForm = (today: string): SchoolFormState => ({
  ...emptyFlightForm(today),
  personsOnBoard: 2,
  classification: 'dual',
  isCrossCountry: false,
  instrumentMinutes: '',
  outsideAto: false,
  needsRepeat: false,
  briefingCompleted: false,
  briefingNotes: '',
})

export default function LogFlightPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { enrolmentId: raw } = useParams()
  const enrolmentId = Number(raw)
  const { user } = useAuth()
  const today = useClubToday()
  const { showSnackbar } = useSnackbar()

  const { data: progress, isLoading, error } = useProgress(enrolmentId)
  const enrolment = progress?.enrolment
  const { data: myLicences = [] } = useLicencesOf(user?.id)
  const { data: studentLicences = [] } = useLicencesOf(enrolment?.studentUserId)
  const aircraftQuery = useFlightLogAircraft()
  const aircraft = aircraftQuery.data ?? []
  const { data: flightTypes = [] } = useFlightTypes()
  const { data: config } = useFlightLogConfig()
  const { timeBase, timeZone } = useTimeBase()
  const { logFlight, attach } = useTrainingMutations(enrolmentId)
  const [files, setFiles] = useState<File[]>([])
  const [searchParams] = useSearchParams()
  const bookingId = Number(searchParams.get('bookingId')) || null
  const { data: lessonBookings = [] } = useLessonBookings(bookingId ? enrolmentId : Number.NaN)
  const booking = lessonBookings.find((b) => b.bookingId === bookingId)
  const { utcMs } = useServerClock()
  // Today in whichever clock the flight is logged in (the club's gates above use the club's date).
  const flightToday = dateIn(utcMs, timeZone)

  const lessons = useMemo(() => progress?.modules.flatMap((m) => m.lessons) ?? [], [progress])
  // Default to the first lesson not yet done, from the first one actually flown.
  // Back from presenting the briefing: the lesson is chosen and the briefing ticked.
  const [lessonChoice, setLessonChoice] = useState<string | null>(searchParams.get('lessonId'))
  const lessonValue = lessonChoice ?? String(nextFlightLesson(lessons)?.lessonId ?? 'extra')
  const lesson = lessons.find((l) => String(l.lessonId) === lessonValue)
  const [classificationChosen, setClassificationChosen] = useState(false)

  const [form, setForm] = useState<SchoolFormState>(() => ({
    ...emptyForm(flightToday),
    briefingCompleted: searchParams.get('briefingDone') === '1',
  }))
  const [ticked, setTicked] = useState<Set<string>>(new Set())
  const set = <K extends keyof SchoolFormState>(key: K, value: SchoolFormState[K]) => setForm((f) => ({ ...f, [key]: value }))
  const setFlight: FlightFieldsProps<SchoolFormState>['set'] = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  // A new flight dated "today" stays today when the member switches clock.
  const previousZone = useRef(timeZone)
  useEffect(() => {
    const from = previousZone.current
    previousZone.current = timeZone
    if (from === timeZone) return
    setForm((f) => (f.date === dateIn(Date.now(), from) ? { ...f, date: dateIn(Date.now(), timeZone) } : f))
  }, [timeZone])

  // The syllabus says whether a lesson is dual or solo; the instructor can
  // still flip it, and then it stays flipped.
  const lessonDefault = lesson?.defaultClassification ?? 'dual'
  const lessonClassification = lessonDefault.startsWith('solo') ? 'solo' : 'dual'
  const lessonCrossCountry = lessonDefault.endsWith('_xc')
  useEffect(() => {
    if (classificationChosen) return
    setForm((f) =>
      f.classification === lessonClassification && f.isCrossCountry === lessonCrossCountry
        ? f
        : {
            ...f,
            classification: lessonClassification,
            isCrossCountry: lessonCrossCountry,
            personsOnBoard: lessonClassification === 'dual' ? Math.max(f.personsOnBoard, 2) : 1,
          },
    )
  }, [lessonClassification, lessonCrossCountry, classificationChosen])

  // Logged from a lesson booking: its day, aircraft, lesson and block times,
  // read in the clock the flight is logged in, as a starting point the
  // instructor corrects. Without one, the aircraft is chosen by hand.
  useEffect(() => {
    if (!booking) return
    const local = (iso: string, opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-CA', { timeZone, ...opts }).format(new Date(iso))
    const hhmm = (iso: string) => local(iso, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    setForm((f) => ({
      ...f,
      date: local(booking.startsAt, { year: 'numeric', month: '2-digit', day: '2-digit' }),
      registration: booking.registration,
      offBlockTime: hhmm(booking.startsAt),
      onBlockTime: hhmm(booking.endsAt),
    }))
    if (booking.lessonId) setLessonChoice(String(booking.lessonId))
  }, [booking, timeZone])
  useEffect(() => {
    if (form.flightType) return
    // A school flight is KOU (koulutus) in the journey log.
    const kou = flightTypes.find((ft) => ft.name === SCHOOL_FLIGHT_TYPE)
    if (kou) set('flightType', kou.name)
    else if (flightTypes.length === 1) set('flightType', flightTypes[0]!.name)
  }, [flightTypes, form.flightType])

  const model = useFlightModel(form, config, aircraft)
  const { blockMinutes, nightMinutes, ifrMinutes, fuel, oil } = model
  const dual = form.classification === 'dual'
  // Instrument time is part of the block time.
  const instrumentMinutes = form.instrumentMinutes ? Number(form.instrumentMinutes) : null
  const instrumentTooLong = instrumentMinutes != null && blockMinutes != null && instrumentMinutes > blockMinutes

  const isBir = enrolment?.courseCode === 'BIR'
  const myGate = instructorGate(toGate(myLicences), today, { courseCode: enrolment?.courseCode })
  const theirGate = studentGate(toGate(studentLicences), today)
  const soloIssue = form.classification === 'solo' ? soloMedicalGate(toGate(studentLicences), today, enrolment?.courseCode) : null
  // Exercises flown together with the chosen one: their items are ticked on this flight too.
  const [combinedIds, setCombinedIds] = useState<number[]>([])
  const combined = lessons.filter((l) => combinedIds.includes(l.lessonId) && l.lessonId !== lesson?.lessonId)
  const preSoloBlocked =
    preSoloGate({
      lessons,
      lessonId: lesson?.lessonId ?? null,
      combinedLessonIds: combined.map((l) => l.lessonId),
      classification: form.classification,
      check: progress?.preSoloCheck ?? null,
    }) !== null

  const ownItems = [lesson, ...combined].flatMap((l) => (l ? l.items.map((item) => ({ ...item, lessonId: l.lessonId, lessonNumber: l.lessonNumber })) : []))
  const heldOver = (progress?.openItems ?? []).filter((i) => i.lessonId !== lesson?.lessonId && !combinedIds.includes(i.lessonId))
  const toggle = (k: string) =>
    setTicked((current) => {
      const next = new Set(current)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })

  // What must be filled before the flight can be logged (fuel and oil are only
  // asked for, below). Shown on the first press of Save rather than by a
  // button that silently stays grey.
  const required = [
    ...model.issues,
    ...(lesson && dual && !form.briefingCompleted ? ['briefing'] : []),
    ...(lesson && ownItems.length > 0 && !ownItems.some((i) => ticked.has(itemKey(i.lessonId, i.itemCode)) || i.completedVia) ? ['items'] : []),
  ]
  const [tried, setTried] = useState(false)
  // True from the first press of Save until the files are up too: the flight
  // exists once logFlight resolves, so a second press would log it twice.
  const [saving, setSaving] = useState(false)
  const canSave = !myGate.blocked && !preSoloBlocked && !instrumentTooLong && !soloIssue && !logFlight.isPending && !saving

  // Fuel and oil still empty: the pilot confirms none was added (or goes back).
  const [missingFuelOil, setMissingFuelOil] = useState<('fuel' | 'oil')[]>([])
  const [openFuelSignal, setOpenFuelSignal] = useState(0)

  const save = async (confirmedFuelOil = false) => {
    if (saving) return
    if (required.length) {
      setTried(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    const missing = unrecordedFuelOil(fuel, oil)
    if (missing.length && !confirmedFuelOil) {
      setMissingFuelOil(missing)
      return
    }
    const body: CreateTrainingFlightDraft = {
      lessonId: lesson ? lesson.lessonId : null,
      classification: form.classification,
      isCrossCountry: form.isCrossCountry,
      instrumentMinutes,
      outsideAto: isBir && form.outsideAto,
      needsRepeat: form.needsRepeat,
      briefingCompleted: form.briefingCompleted,
      briefingNotes: form.briefingNotes || null,
      bookingId,
      combinedLessonIds: combined.map((l) => l.lessonId),
      completedItems: [...ticked].map((k) => {
        const [lessonId, ...rest] = k.split(':')
        return { lessonId: Number(lessonId), itemCode: rest.join(':') }
      }),
      // Who is aboard is the API's call, from the classification.
      flight: {
        registration: form.registration,
        flightType: form.flightType,
        date: form.date,
        timeBase,
        offBlockTime: form.offBlockTime,
        takeoffTime: form.takeoffTime,
        landingTime: form.landingTime,
        onBlockTime: form.onBlockTime,
        departure: form.departure,
        arrival: form.arrival,
        landings: form.landings,
        personsOnBoard: form.personsOnBoard,
        nightMinutes: nightMinutes ?? 0,
        nightLandings: form.nightLandings,
        ifrMinutes: ifrMinutes ?? 0,
        fuelAddedLitres: fuel ?? 0,
        fuelMeasuredLitres: model.measured ?? null,
        oilAddedLitres: oil ?? 0,
        fuelRemainingLitres: form.fuelRemainingLitres,
        remarks: form.remarks || null,
      },
    }
    setSaving(true)
    try {
      const logged = await logFlight.mutateAsync(body)
      // The flight exists now, so its files can go up; a failed one does not undo the flight.
      const results = await Promise.allSettled(files.map((file) => attach.mutateAsync({ flightId: logged.flightId, file })))
      const failed = files.filter((_, i) => results[i]!.status === 'rejected').map((f) => f.name)
      if (failed.length) showSnackbar(t('school.attachmentsFailed', { files: failed.join(', ') }), { severity: 'warning' })
      else showSnackbar(t('school.flightLogged'))
      navigate(`/school/students/${enrolmentId}`)
    } catch (err) {
      setSaving(false)
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Title label={enrolment ? `${t('school.logFlight')} · ${enrolment.studentName}` : t('school.logFlight')} />
      <RemoteContent isLoading={isLoading} error={error}>
        <Stack spacing={2}>
          {tried && required.length ? (
            <Alert severity="error">{t('school.missingTitle', { items: required.map((m) => t(`school.missing_${m}`)).join(', ') })}</Alert>
          ) : null}
          {myGate.blocked ? (
            <Alert severity="warning">
              {t('school.gateInstructor')}: {myGate.issues.map((i) => `${t(`licence.type_${i.licenceType}`)} (${t(`school.gate_${i.reason}`)})`).join(', ')}
            </Alert>
          ) : null}
          {theirGate.blocked ? (
            <Alert severity="warning">
              {t('school.gateStudent')}: {theirGate.issues.map((i) => t(`licence.type_${i.licenceType}`)).join(', ')}
            </Alert>
          ) : null}

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <TextField select label={t('school.lesson')} value={lessonValue} onChange={(e) => setLessonChoice(e.target.value)}>
                  {lessons.map((l) => (
                    <MenuItem key={l.lessonId} value={String(l.lessonId)}>
                      {l.lessonNumber} {l.title}
                      {l.status !== 'not_started' ? ` · ${t(`school.lesson_${l.status}`)}` : ''}
                    </MenuItem>
                  ))}
                  <MenuItem value="extra">{t('school.extraFlight')}</MenuItem>
                </TextField>
                {lesson ? (
                  <Autocomplete
                    multiple
                    size="small"
                    options={lessons.filter((l) => l.lessonId !== lesson.lessonId)}
                    value={combined}
                    onChange={(_, v) => {
                      const keep = new Set(v.map((l) => l.lessonId))
                      // Items ticked under an exercise taken off the flight go with it.
                      const dropped = combinedIds.filter((id) => !keep.has(id))
                      if (dropped.length) setTicked((cur) => new Set([...cur].filter((k) => !dropped.some((id) => k.startsWith(`${id}:`)))))
                      setCombinedIds([...keep])
                    }}
                    getOptionLabel={(l) => `${l.lessonNumber} ${l.title}`}
                    isOptionEqualToValue={(a, b) => a.lessonId === b.lessonId}
                    renderInput={(params) => <TextField {...params} label={t('school.combineWith')} />}
                  />
                ) : null}
                {preSoloBlocked ? <Alert severity="error">{t('school.preSoloNotReleased')}</Alert> : null}
                <FormControlLabel
                  sx={tried && required.includes('briefing') ? { color: 'error.main' } : undefined}
                  control={<Switch checked={form.briefingCompleted} onChange={(e) => set('briefingCompleted', e.target.checked)} />}
                  label={t('school.briefingDone')}
                />
                {form.briefingCompleted ? (
                  <TextField multiline minRows={2} label={t('school.briefingNotes')} value={form.briefingNotes} onChange={(e) => set('briefingNotes', e.target.value)} />
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={form.classification}
                  onChange={(_, v) =>
                    v &&
                    (setClassificationChosen(true),
                    setForm((f) => ({
                      ...f,
                      classification: v,
                      // Dual: instructor and student; solo: the student alone.
                      personsOnBoard: v === 'dual' ? Math.max(f.personsOnBoard, 2) : 1,
                    })))
                  }
                >
                  <ToggleButton value="dual">{t('school.classification_dual')}</ToggleButton>
                  <ToggleButton value="solo">{t('school.classification_solo')}</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body2" color="text.secondary">
                  {t(dual ? 'school.crewNote_dual' : 'school.crewNote_solo', { student: enrolment?.studentName ?? '' })}
                </Typography>
                {soloIssue ? (
                  <Alert severity="error">
                    {t(`school.soloMedical_${soloIssue.reason}`, {
                      requiredClass: soloIssue.requiredClass ? t(`licence.class_${soloIssue.requiredClass}`) : '',
                    })}
                  </Alert>
                ) : null}
                <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Switch checked={form.isCrossCountry} onChange={(e) => set('isCrossCountry', e.target.checked)} />}
                    label={t('school.crossCountry')}
                  />
                  {isBir ? (
                    <FormControlLabel
                      control={<Switch checked={form.outsideAto} onChange={(e) => set('outsideAto', e.target.checked)} />}
                      label={t('school.outsideAto')}
                    />
                  ) : null}
                </Stack>
                {isBir || enrolment?.includesNf ? (
                  <TextField
                    label={t('school.instrumentMinutes')}
                    value={form.instrumentMinutes}
                    onChange={(e) => set('instrumentMinutes', e.target.value.replace(/\D/g, ''))}
                    error={instrumentTooLong}
                    helperText={instrumentTooLong ? t('flightLog.durationTooLong') : undefined}
                    slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                  />
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <FlightFieldsMain
            form={form}
            set={setFlight}
            setForm={setForm}
            model={model}
            aircraft={aircraft}
            flightTypes={flightTypes}
            today={flightToday}
            defaultAirfield={config?.homeBase ?? DEFAULT_HOME_BASE}
            minPersons={dual ? 2 : 1}
            highlight={tried ? required : []}
          />
          <FlightFieldsExtras form={form} set={setFlight} model={model} openFuelSignal={openFuelSignal} />

          {ownItems.length > 0 || heldOver.length > 0 ? (
            <Card variant="outlined" sx={tried && required.includes('items') ? { borderColor: 'error.main', borderWidth: 2 } : undefined}>
              <CardContent>
                {ownItems.length > 0 ? (
                  <>
                    <Typography variant="subtitle2">{t('school.completedOnFlight')}</Typography>
                    {ownItems.map((item) => {
                      const k = itemKey(item.lessonId, item.itemCode)
                      return (
                        <FormControlLabel
                          key={k}
                          sx={{ display: 'flex', alignItems: 'flex-start', my: 0.5 }}
                          control={<Checkbox checked={ticked.has(k) || Boolean(item.completedVia)} disabled={Boolean(item.completedVia)} onChange={() => toggle(k)} sx={{ pt: 0 }} />}
                          label={
                            <Typography variant="body2">
                              {combined.length > 0 ? <b>{item.lessonNumber} </b> : null}
                              {item.description}
                            </Typography>
                          }
                        />
                      )
                    })}
                  </>
                ) : null}
                {heldOver.length > 0 ? (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                      {t('school.heldOver')}
                    </Typography>
                    {heldOver.map((item) => {
                      const k = itemKey(item.lessonId, item.itemCode)
                      return (
                        <FormControlLabel
                          key={k}
                          sx={{ display: 'flex', alignItems: 'flex-start', my: 0.5 }}
                          control={<Checkbox checked={ticked.has(k)} onChange={() => toggle(k)} sx={{ pt: 0 }} />}
                          label={
                            <Typography variant="body2">
                              <b>{item.lessonNumber}</b> {item.description}
                            </Typography>
                          }
                        />
                      )
                    })}
                  </>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="subtitle2">{t('school.attachmentsTitle')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('school.attachmentsHint')}
                </Typography>
                {files.map((file, i) => (
                  <Stack key={`${file.name}:${i}`} direction="row" sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {file.name}
                    </Typography>
                    <IconButton onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))} aria-label={t('common.delete')}>
                      <Icon icon="mdi:close" />
                    </IconButton>
                  </Stack>
                ))}
                <Button component="label" size="small" startIcon={<Icon icon="mdi:paperclip" />} sx={{ alignSelf: 'flex-start' }}>
                  {t('school.attach')}
                  <input
                    hidden
                    multiple
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    onChange={(e) => {
                      const all = Array.from(e.target.files ?? [])
                      const tooBig = all.find((f) => f.size > MAX_ATTACHMENT_BYTES)
                      if (tooBig) showSnackbar(t('school.attachmentTooLarge', { file: tooBig.name }), { severity: 'warning' })
                      const picked = all.filter((f) => f.size <= MAX_ATTACHMENT_BYTES)
                      setFiles((fs) => [...fs, ...picked].slice(0, 10))
                      e.target.value = ''
                    }}
                  />
                </Button>
                {lesson ? (
                  <FormControlLabel
                    control={<Switch checked={form.needsRepeat} onChange={(e) => set('needsRepeat', e.target.checked)} />}
                    label={t('school.needsRepeat')}
                  />
                ) : null}
                <TextField multiline minRows={2} label={t('school.remarks')} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </RemoteContent>

      {/* Stays in reach above the phone's bottom bar. */}
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          left: { xs: 0, md: 240 },
          right: 0,
          bottom: { xs: 'calc(56px + env(safe-area-inset-bottom))', md: 0 },
          p: 1.5,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          zIndex: (theme) => theme.zIndex.appBar - 1,
        }}
      >
        <Button onClick={() => navigate(-1)}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => void save()}>
          {t('school.saveFlight')}
          {blockMinutes != null ? ` · ${formatMinutes(blockMinutes)}` : ''}
        </Button>
      </Paper>
      <FuelOilConfirm
        missing={missingFuelOil}
        onConfirm={() => {
          setMissingFuelOil([])
          void save(true)
        }}
        onBack={() => {
          setMissingFuelOil([])
          setOpenFuelSignal((n) => n + 1)
        }}
      />
    </Box>
  )
}
